import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AppThunk } from '../../../store';
import { SelectOption } from '../Annotation/annotation.types';
import axios from '../../../axios';
import { ToastAnt } from '../../Shared/Toast/ToastAnt';

export type FailureMechanismTemplate = {
  id?: string;
  code: string;
  name: string;
  description: string;
  canDelete: boolean;
  canUpdate: boolean;
  deprecated: boolean;
};

type FailureMechanismTemplateFiltersRequest = {
  codes: string[];
  names: string[];
};

type FailureMechanismTemplateFiltersResponse = {
  codes: SelectOption[];
  names: SelectOption[];
};

type PaggingConfiguration = {
  take: number;
  skip: number;
  getTotal: boolean;
  filters: FailureMechanismTemplateFiltersRequest;
};

type PaggingResult = {
  totalNumber?: number;
  elements: FailureMechanismTemplate[];
  filters: FailureMechanismTemplateFiltersResponse;
};

export const TAKE = 10;

type State = {
  failureMechanismTemplates: Array<FailureMechanismTemplate>;
  codes: SelectOption[];
  selectedCode?: string;
  names: SelectOption[];
  selectedName?: string;
  loading: boolean;
  formLoading: boolean;
  formResult?: boolean;
  totalNumber: number;
  currentPage: number;
  size: number;
};

const initialState: State = {
  failureMechanismTemplates: [],
  loading: false,
  formLoading: false,
  totalNumber: 0,
  currentPage: 0,
  size: TAKE,
  codes: [],
  names: []
};

const failureMechanismTemplateSlice = createSlice({
  name: 'findingTypeTemplate',
  initialState,
  reducers: {
    startLoading(state) {
      state.loading = true;
    },
    endLoading(state) {
      state.loading = false;
    },
    startFormLoading(state) {
      state.formLoading = true;
    },
    endFormLoading(state) {
      state.formLoading = false;
    },
    setFormResult(state, action: PayloadAction<boolean | undefined>) {
      state.formResult = action.payload;
    },
    populatePaggingFields(state, action: PayloadAction<PaggingResult>) {
      const { elements, filters, totalNumber } = action.payload;
      state.failureMechanismTemplates = elements;
      state.codes = filters.codes;
      state.names = filters.names;

      if (totalNumber) {
        state.totalNumber = totalNumber;
      }

      state.loading = false;
    },
    setSizeAndPage: (state, action: PayloadAction<{ size: number; page: number }>) => {
      const { page, size } = action.payload;
      state.size = size;
      state.currentPage = page;
    },
    setSelectedCode(state, action: PayloadAction<string | undefined>) {
      state.selectedCode = action.payload;
    },
    setSelectedName(state, action: PayloadAction<string | undefined>) {
      state.selectedName = action.payload;
    }
  }
});

export const {
  startLoading,
  endLoading,
  startFormLoading,
  endFormLoading,
  setFormResult,
  populatePaggingFields,
  setSizeAndPage,
  setSelectedCode,
  setSelectedName
} = failureMechanismTemplateSlice.actions;

export default failureMechanismTemplateSlice.reducer;

export const getFailureMechanismTemplates =
  (take: number, newPage: number): AppThunk =>
  async (dispatch, getState) => {
    const { selectedCode, selectedName } = getState().failureMechanismTemplate;

    dispatch(startLoading());

    dispatch(setSizeAndPage({ page: newPage, size: take }));

    const config: PaggingConfiguration = {
      take: take,
      skip: (newPage - 1) * take,
      getTotal: true,
      filters: {
        codes: selectedCode ? [selectedCode] : [],
        names: selectedName ? [selectedName] : []
      }
    };

    try {
      const response = await axios.post<PaggingResult>('/failuremechanism/search', config);
      dispatch(populatePaggingFields(response.data));
    } catch (error) {
      console.log(error);
    }

    dispatch(endLoading());
  };

export const createFailureMechanismTemplate =
  (template: FailureMechanismTemplate): AppThunk =>
  async (dispatch, getState) => {
    const { size } = getState().failureMechanismTemplate;

    dispatch(startFormLoading());

    try {
      await axios.post('/failuremechanism', template);

      dispatch(getFailureMechanismTemplates(size, 1));
      dispatch(setFormResult(true));
      ToastAnt('Saved.');
    } catch (error) {
      handleError(error);
      dispatch(setFormResult(false));
    }

    dispatch(endFormLoading());
  };

export const updateFailureMechanismTemplate =
  (template: FailureMechanismTemplate): AppThunk =>
  async (dispatch, getState) => {
    const { size } = getState().failureMechanismTemplate;

    dispatch(startFormLoading());

    try {
      await axios.put('/failuremechanism', template);

      dispatch(getFailureMechanismTemplates(size, 1));
      dispatch(setFormResult(true));
      ToastAnt('Saved.');
    } catch (error) {
      dispatch(setFormResult(false));
      handleError(error);
    }

    dispatch(endFormLoading());
  };

export const deleteFailureMechanismTemplate =
  (id: string): AppThunk =>
  async (dispatch, getState) => {
    const { size } = getState().failureMechanismTemplate;

    dispatch(startLoading());

    try {
      await axios.delete(`/failuremechanism/${id}`);

      dispatch(getFailureMechanismTemplates(size, 1));
      ToastAnt('Deleted.');
    } catch (error) {
      console.log(error);
      ToastAnt('An error occurred.');
    }

    dispatch(endLoading());
  };
function handleError(error: any) {
  console.log(error);
  if (error && error.response && error.response.data && error.response.data.Message) {
    ToastAnt(error.response.data.Message);
  } else {
    ToastAnt('An error occurred.');
  }
}
